import { URL_SUPPORT_TERMS, URL_DASHBOARD } from '@seek/adv-constants';
import {
  type LocationSuggestion,
  JobLocation as JobLocationField,
} from '@seek/job-location';
import { useTranslations } from '@vocab/react';
import {
  Actions,
  Alert,
  Box,
  Button,
  ButtonIcon,
  Column,
  Columns,
  Heading,
  IconAdd,
  IconClear,
  Inline,
  Notice,
  Stack,
  Text,
  TextLink,
} from 'braid-design-system';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';

import { ADD_LOCATION_PRESSED } from 'src/app/shared/dataAnalytics/schema/constants';
import { useConfig } from 'src/app/shared/hooks/useConfig';
import { calculateTax, getSubTotal } from 'src/app/utils/multiLocation';
import getTrackingAdapter from 'src/app/v1/dataAnalytics';
import { MultiLocationOrderSummary } from 'src/app/v1/views/pages/MultiLocation/components/MultiLocationOrderSummary';
import {
  locationIds,
  multiLocations,
} from 'src/app/v1/views/pages/MultiLocation/constants/multiLocation';
import type {
  LocationField,
  LocationKey,
  LocationObject,
  OrderTotals,
} from 'src/app/v1/views/pages/MultiLocation/type';
import type { AppState } from 'src/types/appState';

import { DisableLocations } from '../DisableLocations';

import translations from './.vocab';

export const JobLocationFields = ({
  handleOnSuggestionSelected,
  handleButtonClick,
  requestValidation,
  setRequestValidation,
  isLoading,
  locations,
  setLocations,
  locationCount,
  setLocationCount,
}: {
  handleOnSuggestionSelected: (
    id: LocationKey,
    suggestion: LocationSuggestion,
  ) => void;
  handleButtonClick: () => void;
  requestValidation: boolean;
  setRequestValidation: React.Dispatch<React.SetStateAction<boolean>>;
  isLoading: boolean;
  locations: LocationObject;
  setLocations: React.Dispatch<React.SetStateAction<LocationObject>>;
  locationCount: number;
  setLocationCount: React.Dispatch<React.SetStateAction<number>>;
}) => {
  const { t } = useTranslations(translations);

  const { seekAdvertiserId, hidePrice } = useSelector((state: AppState) => ({
    seekAdvertiserId: state.session.seekAdvertiserId,
    hidePrice: state.session.hirerAccount?.hidePrice,
  }));

  const [locationFields, setLocationFields] = useState<Array<LocationField>>([
    {
      locationKey: locationIds[0],
      elementKey: uuidv4(),
    },
  ]);
  const [orderTotals, setOrderTotals] = useState<OrderTotals>({
    subTotal: '$0',
    tax: '$0',
    total: '$0',
  });

  const { getLanguage } = useConfig();
  const newLocations: LocationObject = JSON.parse(
    JSON.stringify(multiLocations),
  );

  useEffect(() => {
    const subTotal = getSubTotal(locations);
    const tax = calculateTax(locations);
    const total = subTotal + tax;
    setOrderTotals({
      subTotal: `$${subTotal.toFixed(2)}`,
      tax: `$${tax.toFixed(2)}`,
      total: `$${total.toFixed(2)}`,
    });
  }, [locations]);

  const addLocationPlaceholder = () => {
    const eventData = {
      eventName: ADD_LOCATION_PRESSED,
    };
    getTrackingAdapter.sendLinkEvent(eventData);

    setLocationCount(locationCount + 1);
    setRequestValidation(false);
    setLocationFields([
      ...locationFields,
      {
        locationKey: locationIds[locationCount],
        elementKey: uuidv4(),
      },
    ]);
  };

  const handleOnClear = (elementKey: string) => {
    const filteredFields: Array<LocationField> = locationFields.filter(
      (field) => elementKey !== field.elementKey,
    );

    filteredFields.map(
      (field, key) =>
        (newLocations[locationIds[key]] = {
          jobLocationId: locations[field.locationKey].jobLocationId,
          jobLocationText: locations[field.locationKey].jobLocationText,
          isErrorWhenPosting: false,
          jobPrice: locations[field.locationKey].jobPrice,
          tax: locations[field.locationKey].tax,
        }),
    );

    setLocations({ ...newLocations });
    setLocationFields(filteredFields);
    setLocationCount(locationCount - 1);
  };

  const getLocationsEntered = (loc: LocationObject) => {
    let k: keyof typeof loc;
    let count = 0;
    for (k in loc) {
      if (loc.hasOwnProperty(k)) {
        if (loc[k].jobLocationId) {
          count++;
        }
      }
    }
    return count;
  };

  return (
    <Stack space="xlarge">
      <Stack space="large">
        <Heading level="3">{t('job-location-header')}</Heading>
        <Stack space="small">
          <Columns space="xlarge">
            <Column width="3/5">
              <Text weight="strong">{t('job-location-label')}</Text>
            </Column>
          </Columns>
          <DisableLocations disabled={isLoading}>
            <Stack space="xsmall">
              {locationFields.map(({ elementKey }, key) => (
                <Columns
                  key={elementKey}
                  space="medium"
                  alignY="center"
                  data={{
                    testid: `job-location-widget-${key}`,
                  }}
                >
                  <Column width="2/3">
                    <JobLocationField
                      id={`job-location-input-${key}`}
                      isValidationRequested={requestValidation}
                      onChange={(loc) => {
                        handleOnSuggestionSelected(locationIds[key], loc);
                      }}
                      value={{
                        value: locations[locationIds[key]].jobLocationId || '',
                        text: locations[locationIds[key]].jobLocationText || '',
                      }}
                      advertiserId={seekAdvertiserId ?? ''}
                      countries={{
                        only: ['AU', 'NZ'],
                      }}
                      language={getLanguage()}
                    />
                  </Column>
                  <Column width="content">
                    <Inline space="small" alignY="center">
                      {locationIds[key] !== 'location1' && (
                        <Box alignItems="center" paddingBottom="small">
                          <ButtonIcon
                            variant="transparent"
                            icon={<IconClear tone="secondary" />}
                            label="Remove Location"
                            id={elementKey}
                            data={{
                              testid: `remove-location-button`,
                            }}
                            onClick={() => handleOnClear(elementKey)}
                          />
                        </Box>
                      )}
                    </Inline>
                  </Column>
                </Columns>
              ))}
            </Stack>
            {locationCount < 5 ? (
              <Box paddingTop="xsmall">
                <Inline space="xlarge" alignY="center">
                  <Button
                    onClick={addLocationPlaceholder}
                    data={{ testid: 'add-another-location-button' }}
                    variant="ghost"
                  >
                    <IconAdd />
                    {'  '}
                    {t('job-location-add-location-button-text')}
                  </Button>
                  <Text>{locationCount}/5</Text>
                </Inline>
              </Box>
            ) : (
              <Notice tone="info">
                <Text>{t('job-location-max-location-text')}</Text>
              </Notice>
            )}
          </DisableLocations>
        </Stack>
      </Stack>
      {getLocationsEntered(locations) >= 1 && !hidePrice && (
        <MultiLocationOrderSummary
          locationFields={locationFields}
          locations={locations}
          orderTotals={orderTotals}
        />
      )}
      <Stack space="medium">
        {getLocationsEntered(locations) >= 1 && (
          <Alert tone="info">
            <Text>{t('job-location-post-job-alert-text')}</Text>
            <Box
              style={{
                height: '22px',
              }}
            />
            <Text>
              {t('job-location-tax-alert-text', {
                Link: (children) => (
                  <TextLink href={URL_DASHBOARD} weight="weak" target="_blank">
                    {children}
                  </TextLink>
                ),
              })}
            </Text>
          </Alert>
        )}
        <Text size="small">
          {t('job-location-post-job-terms-text', {
            Link: (children) => (
              <TextLink href={URL_SUPPORT_TERMS} weight="weak" target="_blank">
                {children}
              </TextLink>
            ),
          })}
        </Text>
        <Actions>
          <Button
            tone={isLoading ? 'neutral' : 'brandAccent'}
            type="submit"
            id="submit-form"
            loading={isLoading}
            onClick={handleButtonClick}
          >
            {(isLoading &&
              t('job-location-post-ad-button-text-processing', {
                jobPostedLocationCount: locationCount,
              })) ||
              (getLocationsEntered(locations) > 1
                ? t('job-location-post-ad-button-text', {
                    jobPostedLocationCount: locationCount,
                  })
                : t('job-location-post-ad-button-text', {
                    jobPostedLocationCount: 1,
                  }))}
          </Button>
        </Actions>
      </Stack>
    </Stack>
  );
};
